import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  Copy,
  Package,
  Ruler,
  Weight,
  X
} from 'lucide-react';
import { Can } from '../components/Auth/Can';
import {
  selectFilteredProducts,
  selectProductsFilters,
  selectProductsLoading,
  selectProductsError,
  fetchProducts,
  setFilters,
  clearFilters,
  createProduct,
  updateProduct,
  deleteProduct
} from '../store/slices/productsSlice';
import { fetchTiposCaja, selectActiveTiposCaja } from '../store/slices/tipoCajaSlice';
import AddProductModal from '../components/Products/AddProductModal';
import EditProductModal from '../components/Products/EditProductModal';
import DeleteProductModal from '../components/Products/DeleteProductModal';

const Products = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const products = useSelector(selectFilteredProducts);
  const filters = useSelector(selectProductsFilters);
  const loading = useSelector(selectProductsLoading);
  const error = useSelector(selectProductsError);
  const tiposCaja = useSelector(selectActiveTiposCaja);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Cargar productos y tipos de caja al montar el componente
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchTiposCaja({ activo: true }));
  }, [dispatch]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setShowFilters(false);
  };

  const handleAddProduct = async (productData) => {
    await dispatch(createProduct(productData)).unwrap();
    dispatch(fetchProducts());
  };

  const handleEditProduct = async (id, productData) => {
    await dispatch(updateProduct({ id, productData })).unwrap();
    dispatch(fetchProducts());
  };

  const handleDeleteProduct = async (id) => {
    await dispatch(deleteProduct(id)).unwrap();
    dispatch(fetchProducts());
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const openCloneModal = (product) => {
    setSelectedProduct(product);
    setShowAddModal(true);
  };


  return (
    <div className="products-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('products.title')}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {t('products.subtitle')}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder={`${t('common.search')} productos...`}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-actions">
            <button 
              className={`btn btn-outline ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={20} />
              Filtros
            </button>
            
            {(filters.search || filters.weight !== 'all' || filters.length !== 'all' || filters.boxType !== 'all' || filters.activo !== undefined) && (
              <button 
                className="btn btn-outline"
                onClick={handleClearFilters}
              >
                <X size={20} />
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="advanced-filters">
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label">Peso (gr)</label>
                <select
                  className="form-select"
                  value={filters.weight}
                  onChange={(e) => handleFilterChange('weight', e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="250">250g</option>
                  <option value="500">500g</option>
                  <option value="750">750g</option>
                  <option value="1000">1000g</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">{t('products.length')}</label>
                <select
                  className="form-select"
                  value={filters.length}
                  onChange={(e) => handleFilterChange('length', e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="40">40cm</option>
                  <option value="50">50cm</option>
                  <option value="60">60cm</option>
                  <option value="70">70cm</option>
                  <option value="80">80cm</option>
                  <option value="100">100cm</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Tipo de Caja</label>
                <select
                  className="form-select"
                  value={filters.boxType}
                  onChange={(e) => handleFilterChange('boxType', e.target.value)}
                >
                  <option value="all">{t('common.all')}</option>
                  {tiposCaja.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.tipo} - {tipo.descripcion}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select
                  className="form-select"
                  value={filters.activo === undefined ? 'all' : filters.activo ? 'true' : 'false'}
                  onChange={(e) => handleFilterChange('activo', e.target.value === 'all' ? undefined : e.target.value === 'true')}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p className="text-gray-600">
          Mostrando {products.length} producto{products.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card bg-red-50 border-red-200">
          <div className="text-red-700">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p className="text-gray-600 mt-4">Cargando productos...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Especificaciones</th>
                  <th>Precio CLP</th>
                  <th>Precio USD</th>
                  <th>Costo USD</th>
                  <th>Empaque</th>
                  <th>Estado</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="product-info">
                        <div className="product-icon">
                          <Package size={20} />
                        </div>
                        <div>
                          <div className="font-medium">{product.nombre}</div>
                          <div className="text-sm text-gray-500">{product.code}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="specs-info">
                        {product.category === 'Despacho' ? (
                          <span className="status-badge" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5', fontSize: '0.875rem' }}>
                            Despacho
                          </span>
                        ) : (
                          <>
                            <div className="spec-item">
                              <Ruler size={14} />
                              <span>{product.large_cm}cm</span>
                            </div>
                            <div className="spec-item">
                              <Weight size={14} />
                              <span>{product.weigh_bunches_gr ? (product.weigh_bunches_gr / 1000).toFixed(2) : '0.00'}kg</span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {product.large_cm && product.tipo_caja 
                                ? `${product.large_cm}cm ${product.tipo_caja.descripcion}`
                                : product.category || ''}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="price-info">
                        <span className="font-medium text-lg">
                          {product.precio_unitario 
                            ? new Intl.NumberFormat('es-CL', { 
                                style: 'currency', 
                                currency: 'CLP',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(product.precio_unitario)
                            : '$0'}
                        </span>
                        <div className="text-sm text-gray-500">CLP</div>
                      </div>
                    </td>
                    <td>
                      <div className="price-info">
                        <span className="font-medium text-lg">
                          {product.precio_fob_mia 
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(product.precio_fob_mia)
                            : '$0.00'}
                        </span>
                        <div className="text-sm text-gray-500">USD</div>
                      </div>
                    </td>
                    <td>
                      <div className="price-info">
                        <span className="font-medium text-lg">
                          {product.costo
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(product.costo)
                            : '$0.00'}
                        </span>
                        <div className="text-sm text-gray-500">USD</div>
                      </div>
                    </td>
                    <td>
                      <div className="packaging-info">
                        {product.category === 'Despacho' ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <>
                            <div className="font-medium">{product.bunches_box || 0} ramos/caja</div>
                            <div className="text-sm text-gray-500">
                              {product.tipo_caja 
                                ? `${product.tipo_caja.tipo} - ${product.tipo_caja.descripcion}`
                                : ''}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${product.activo !== false ? 'status-active' : 'status-inactive'}`}>
                        {product.activo !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Can I="clone" a="products:management">
                          <button 
                            className="action-btn clone"
                            onClick={() => openCloneModal(product)}
                            title="Clonar producto"
                          >
                            <Copy size={16} />
                          </button>
                        </Can>
                        <Can I="update" a="products:management">
                          <button 
                            className="action-btn edit"
                            onClick={() => openEditModal(product)}
                            title="Editar producto"
                          >
                            <Edit2 size={16} />
                          </button>
                        </Can>
                        <Can I="delete" a="products:management">
                          <button 
                            className="action-btn delete"
                            onClick={() => openDeleteModal(product)}
                            title="Eliminar producto"
                          >
                            <Trash2 size={16} />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mt-4">
              No se encontraron productos
            </h3>
            <p className="text-gray-500 mt-2">
              Ajusta los filtros o agrega nuevos productos
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }
        
        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-4);
          margin-bottom: var(--spacing-4);
        }
        
        .search-bar {
          position: relative;
          flex: 1;
          max-width: 400px;
        }
        
        .search-bar svg {
          position: absolute;
          left: var(--spacing-3);
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }
        
        .search-input {
          width: 100%;
          padding: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-10);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: var(--font-size-base);
          transition: border-color 0.2s ease-in-out;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.1);
        }
        
        .filter-actions {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .btn.active {
          background-color: var(--primary-blue);
          color: white;
        }
        
        .advanced-filters {
          padding-top: var(--spacing-4);
          border-top: 1px solid var(--gray-200);
        }
        
        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-4);
        }
        
        .results-summary {
          margin-bottom: var(--spacing-4);
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .product-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }
        
        .product-icon {
          width: 40px;
          height: 40px;
          background-color: var(--secondary-green);
          color: white;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .specs-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .spec-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }
        
        .price-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .packaging-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .action-btn {
          padding: var(--spacing-2);
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        }
        
        .action-btn.edit {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.edit:hover {
          background-color: var(--primary-blue);
          color: white;
        }
        
        .action-btn.delete {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.clone {
          background-color: var(--gray-100);
          color: var(--gray-600);
        }
        
        .action-btn.clone:hover {
          background-color: var(--secondary-green);
          color: white;
        }
        
        .action-btn.delete:hover {
          background-color: var(--error);
          color: white;
        }
        
        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-4);
        }
        
        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--gray-200);
          border-top: 4px solid var(--primary-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }
          
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-bar {
            max-width: none;
          }
          
          .filter-grid {
            grid-template-columns: 1fr;
          }
          
          .table {
            font-size: var(--font-size-sm);
          }
          
          .product-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
          
          .product-icon {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>

      {/* Modals */}
      {showAddModal && (
        <AddProductModal
          initialData={selectedProduct}
          onClose={() => {
            setShowAddModal(false);
            setSelectedProduct(null);
          }}
          onSave={handleAddProduct}
        />
      )}

      {showEditModal && selectedProduct && (
        <EditProductModal
          product={selectedProduct}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          onSave={handleEditProduct}
        />
      )}

      {showDeleteModal && selectedProduct && (
        <DeleteProductModal
          product={selectedProduct}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedProduct(null);
          }}
          onDelete={handleDeleteProduct}
        />
      )}
    </div>
  );
};

export default Products;
